import { defineStore } from 'pinia'
import axios from 'axios'

import type { AccessTokenOnly, TokenPair, User } from '@/types/api'

const STORAGE_KEY = 'guess365_auth_v1'

interface PersistedState {
  access_token: string | null
  refresh_token: string | null
  user: User | null
  remember: boolean
}

const EMPTY: PersistedState = {
  access_token: null,
  refresh_token: null,
  user: null,
  remember: true,
}

/**
 * "Remember me" decides WHERE the session lives:
 *  - remember=true  → localStorage  (survives closing the browser)
 *  - remember=false → sessionStorage (cleared when the tab/browser closes)
 * We probe sessionStorage first so a non-remembered tab session takes priority.
 */
function loadPersisted(): PersistedState {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(STORAGE_KEY)
      if (raw) return { ...EMPTY, ...(JSON.parse(raw) as PersistedState) }
    } catch {
      // ignore malformed entries and fall through
    }
  }
  return { ...EMPTY }
}

function persist(state: PersistedState): void {
  const keep = state.remember ? localStorage : sessionStorage
  const drop = state.remember ? sessionStorage : localStorage
  drop.removeItem(STORAGE_KEY)
  keep.setItem(STORAGE_KEY, JSON.stringify(state))
}

function clearAll(): void {
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    const p = loadPersisted()
    return {
      accessToken: p.access_token,
      refreshToken: p.refresh_token,
      user: p.user as User | null,
      remember: p.remember,
      loading: false,
    }
  },
  getters: {
    isAuthenticated: (s): boolean => Boolean(s.accessToken),
    role: (s): string | null => s.user?.role ?? null,
    // 4 roles: guest / user / manage / superadmin. Capability gates mirror the
    // backend (permissions.py). These are coarse UI hints; the backend is the
    // final authority (folder ACL + role gates).
    isSuperadmin: (s): boolean => s.user?.role === 'superadmin',
    // manage or superadmin — can delete + define dataset versions.
    isManager: (s): boolean => !!s.user && ['superadmin', 'manage'].includes(s.user.role),
    // Members (user/manage/superadmin) may upload + set default split.
    canCurate: (s): boolean =>
      !!s.user && ['superadmin', 'manage', 'user'].includes(s.user.role),
    // Downloading the original file — everyone except guest.
    canDownload: (s): boolean =>
      !!s.user && ['superadmin', 'manage', 'user'].includes(s.user.role),
    // Deleting photos / defining dataset versions — manage or superadmin only.
    canDelete: (s): boolean => !!s.user && ['superadmin', 'manage'].includes(s.user.role),
    canDefineDataset: (s): boolean =>
      !!s.user && ['superadmin', 'manage'].includes(s.user.role),
  },
  actions: {
    async login(email: string, password: string, remember = true): Promise<void> {
      this.loading = true
      try {
        this.remember = remember
        // FastAPI OAuth2PasswordRequestForm requires application/x-www-form-urlencoded.
        const baseURL = import.meta.env.VITE_API_BASE_URL || '/api'
        const body = new URLSearchParams()
        body.set('username', email)
        body.set('password', password)
        const { data } = await axios.post<TokenPair>(`${baseURL}/auth/login`, body, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })
        this.accessToken = data.access_token
        this.refreshToken = data.refresh_token

        // Fetch /me right after login so role-based UI works.
        const me = await axios.get<User>(`${baseURL}/me`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
        })
        this.user = me.data
        this._persist()
      } finally {
        this.loading = false
      }
    },

    /** Used by the axios interceptor on 401. Returns the new access token, or null on failure. */
    async refresh(): Promise<string | null> {
      if (!this.refreshToken) return null
      try {
        const baseURL = import.meta.env.VITE_API_BASE_URL || '/api'
        const { data } = await axios.post<AccessTokenOnly>(`${baseURL}/auth/refresh`, {
          refresh_token: this.refreshToken,
        })
        this.accessToken = data.access_token
        this._persist()
        return data.access_token
      } catch {
        return null
      }
    },

    async fetchMe(): Promise<void> {
      // Re-hydrate user after a page reload (we trust accessToken from storage).
      if (!this.accessToken) return
      const { apiClient } = await import('@/api/client')
      const { data } = await apiClient.get<User>('/me')
      this.user = data
      this._persist()
    },

    logout(): void {
      this.accessToken = null
      this.refreshToken = null
      this.user = null
      clearAll()
    },

    _persist(): void {
      persist({
        access_token: this.accessToken,
        refresh_token: this.refreshToken,
        user: this.user,
        remember: this.remember,
      })
    },
  },
})
